/**
 * @file Functions related to edges: creation, reconnection, derivation, propagation
 */

import { NodeSingular } from 'cytoscape';
import { message } from 'antd';
import {
  currentDiagram,
  propagation,
  PropagationEnum,
  StateInterface,
} from '../components/App';
import { cy } from '../components/DiagramCanvas';
import { diagramTreeRoot } from '../model/diagram-tree-model';
import {
  derivedEdgeArray,
  EdgeType,
  hierarchicalStructuralEdges,
  LinkNegation,
  LinkQualifier,
  MMEdge,
  originalEdgeArray,
} from '../model/edge-model';
import { masterModelRoot, MMNode, NodeType } from '../model/node-model';
import { eleCounter } from './elementCounter';
import { addEdge, refreshStructuralEdgeStyles } from './general';
import {
  getAllowedEdgeTypesByMMNodes,
  getInvalidEdgeMessageByMMNodes,
} from '../utils/opl-rules';

//global endpoints, set or unset by following functions
let sourceNode: NodeSingular | null = null;
let targetNode: NodeSingular | null = null;

// 用于创建连线时获取当前端点信息（仅用于 UI 展示）
export const getEdgeCreateDirectionInfo = () => {
  if (!sourceNode || !targetNode) return null;
  return {
    sourceId: sourceNode.id(),
    targetId: targetNode.id(),
    sourceLabel:
      sourceNode.data('label') ||
      sourceNode.data('displayLabel') ||
      sourceNode.id(),
    targetLabel:
      targetNode.data('label') ||
      targetNode.data('displayLabel') ||
      targetNode.id(),
    sourceType: sourceNode.data('type'),
    targetType: targetNode.data('type'),
    sourceMMRef: sourceNode.data('MMRef') as MMNode | null,
    targetMMRef: targetNode.data('MMRef') as MMNode | null,
  };
};

// 删除同向、同端点的已存在结构关系边，避免重复
const removeExistingStructuralEdge = (source: MMNode, target: MMNode) => {
  if (!cy) return;
  const existing = cy
    .edges()
    .filter(
      ele =>
        hierarchicalStructuralEdges.includes(ele.data('type')) &&
        ele.source().id() === source.id &&
        ele.target().id() === target.id
    );

  existing.forEach(edge => {
    const mmRef = edge.data('MMRef') as MMEdge;
    if (mmRef) {
      if (mmRef.originalEdges?.length) {
        originalEdgeArray.removeOriginalEdges(mmRef.originalEdges);
        mmRef.originalEdges.forEach(e => e.removeAllDerived?.());
      } else {
        originalEdgeArray.removeEdge(mmRef);
        mmRef.removeAllDerived?.();
      }
    }
    edge.remove();
  });
};

const findExistingVisibleEdge = (
  source: MMNode,
  target: MMNode,
  type: EdgeType,
  label: string,
  linkQualifier: LinkQualifier,
  linkNegation: LinkNegation
) => {
  if (!cy) return null;
  const existing = cy
    .edges()
    .filter(
      ele =>
        ele.data('display') !== 'none' &&
        ele.source().id() === source.id &&
        ele.target().id() === target.id &&
        ele.data('type') === type &&
        (ele.data('label') || '') === label &&
        (ele.data('linkQualifier') || LinkQualifier.None) === linkQualifier &&
        (ele.data('linkNegation') || LinkNegation.None) === linkNegation
    );
  return existing.length > 0 ? existing[0] : null;
};

/**
 * Function to be invoked on edge create start event - right click and drag.
 * The edge preview is handled by edgehandles extesion.
 * @param eh - Edgehandles extension instance
 * @param evt - Event object
 */
export const edgeCreateStart = (eh: any, evt: Event) => {
  sourceNode = evt.target as unknown as NodeSingular;
  console.log('[EdgeCreate] Start - sourceNode:', sourceNode?.id());
  console.log('[EdgeCreate] eh.active before start:', eh.active);
  // 阻止节点拖动，避免与 edgehandles 冲突
  sourceNode?.ungrabify();
  sourceNode?.addClass('eh-source');
  eh.start(sourceNode);
  console.log('[EdgeCreate] eh.active after start:', eh.active);
};

/**
 * Stopping edge creation
 * @param eh - Edgehandles extension instance
 */
export const edgeCreateStop = (eh: any) => {
  eh.stop();
};

/**
 * Color element that is dragged over with mouse on while creating an edge.
 * @param evt - Event object
 */
export const edgeCreateDragOverElement = (evt: any) => {
  console.log(
    '[EdgeCreate] DragOver - target:',
    evt.target?.id?.() || evt.target
  );
  targetNode = evt.target;
  if (targetNode !== sourceNode) {
    targetNode?.addClass('eh-hover');
  }
};

/**
 * Reset element fill while the element is not dragged over while creating an edge.
 * @param evt - Event object
 */
export const edgeCreateDragOutOfElement = (evt: any) => {
  console.log(
    '[EdgeCreate] DragOut - target:',
    evt.target?.id?.() || evt.target
  );
  evt.target.removeClass('eh-hover');
  targetNode = null;
};

/**
 * Validate before creating an edge, if ok display edge type selection modal.
 * @param callback - Dispatch reducer function
 */
export const edgeCreateValidate = (callback: Function) => {
  console.log(
    '[EdgeCreate] Validate - sourceNode:',
    sourceNode?.id(),
    'targetNode:',
    targetNode?.id()
  );
  // 恢复源节点的可拖动状态
  sourceNode?.grabify();
  sourceNode?.removeClass('eh-hover');
  targetNode?.removeClass('eh-hover');

  if (sourceNode == null || targetNode == null || sourceNode === targetNode) {
    console.log(
      '[EdgeCreate] Validate - skipped (sourceNode or targetNode is null or same)'
    );
    return;
  }

  // 连线限制验证
  const sourceMMRef = sourceNode.data('MMRef');
  const targetMMRef = targetNode.data('MMRef');

  // 判断是否为状态或值类型节点
  const isStateOrValue = (type: string) =>
    type === NodeType.State || type === NodeType.Value;
  const isValue = (type: string) => type === NodeType.Value;

  // 限制 0: 值节点不支持连线
  if (isValue(sourceMMRef?.type) || isValue(targetMMRef?.type)) {
    message.warning('值节点不支持连线');
    return;
  }

  // 限制 1: 相同父节点下的状态/值节点不能互相连线
  if (sourceNode.isChild() && targetNode.isChild()) {
    const sourceParent = sourceNode.parent();
    const targetParent = targetNode.parent();

    // 如果两个节点都是状态/值节点且有相同的父节点
    if (
      isStateOrValue(sourceMMRef?.type) &&
      isStateOrValue(targetMMRef?.type) &&
      sourceParent.length > 0 &&
      targetParent.length > 0 &&
      sourceParent[0].id() === targetParent[0].id()
    ) {
      console.log(
        '[EdgeCreate] Validate - blocked: same parent state/value nodes cannot connect'
      );
      message.warning('同一分组内的节点不能互相连线');
      return;
    }
  }

  // 限制 2: 状态/值节点不能与父节点连线
  // 检查 source 是否是 target 的父节点
  if (targetNode.isChild() && isStateOrValue(targetMMRef?.type)) {
    const targetParent = targetNode.parent();
    if (targetParent.length > 0 && targetParent[0].id() === sourceNode.id()) {
      console.log(
        '[EdgeCreate] Validate - blocked: parent-child connection not allowed (source is parent)'
      );
      message.warning('子节点不能与父节点连线');
      return;
    }
  }

  // 检查 target 是否是 source 的父节点
  if (sourceNode.isChild() && isStateOrValue(sourceMMRef?.type)) {
    const sourceParent = sourceNode.parent();
    if (sourceParent.length > 0 && sourceParent[0].id() === targetNode.id()) {
      console.log(
        '[EdgeCreate] Validate - blocked: parent-child connection not allowed (target is parent)'
      );
      message.warning('子节点不能与父节点连线');
      return;
    }
  }

  // 限制 3: 组合不在定义内则不允许创建
  const allowedTypes = getAllowedEdgeTypesByMMNodes(
    sourceMMRef as MMNode,
    targetMMRef as MMNode
  );
  if (!allowedTypes.length) {
    message.warning(
      getInvalidEdgeMessageByMMNodes(
        sourceMMRef as MMNode,
        targetMMRef as MMNode
      ) || '当前节点组合不支持连线类型'
    );
    return;
  }

  console.log('[EdgeCreate] Validate - calling callback');
  callback();
};

/**
 * Reset after user cancel (closing of edge type selection modal).
 */
export const edgeCreateCancel = () => {
  // 恢复源节点的可拖动状态
  sourceNode?.grabify();
  targetNode?.removeClass('eh-hover');
  sourceNode = null;
  targetNode = null;
};

/**
 * Is a child in a compound node (subprocess or a state).
 * @param node - Master model node
 * @returns - True if is subelement, false if not
 */
const isSubElement = (node: MMNode) => {
  return cy.getElementById(node.id).isChild();
};

/**
 * Determine if edge endpoints are related, i.e., parent and child in structural relationship,
 * 2 subprocesses of the same process, object and its states.
 * @param source - Master model source
 * @param target - Master model target
 * @returns - True if related, false if not
 */
const areRelated = (source: MMNode, target: MMNode): boolean => {
  if (
    (source.isStructurePart &&
      originalEdgeArray.findStructuralParent(source) === target) ||
    (target.isStructurePart &&
      originalEdgeArray.findStructuralParent(target) === source)
  ) {
    return true;
  }
  if (
    cy.getElementById(source.id).parent() ===
    cy.getElementById(target.id).parent()
  ) {
    return true;
  }
  return false;
};

/**
 * Create a new derived edge based on the given original edge, link them together.
 * @param source - Master model source mpde
 * @param target - Master model target node
 * @param originalEdge - Master mode original edge
 * @returns - Reference to newly created master model derived edge, or null if source/target invalid
 */
const addDerivedEdge = (
  source: MMNode | null,
  target: MMNode | null,
  originalEdge: MMEdge
): MMEdge | null => {
  // 防御性检查：source 或 target 为 null 时不创建 derived edge
  if (!source || !target) {
    console.warn(
      `[OPMCV-EDGE] addDerivedEdge 跳过: source=${source?.id ?? 'NULL'}, target=${target?.id ?? 'NULL'}, originalEdgeId=${originalEdge.id}`
    );
    return null;
  }
  const derivedEdge = new MMEdge(
    originalEdge.id,
    source,
    target,
    originalEdge.type,
    false,
    originalEdge,
    originalEdge.label,
    originalEdge.linkQualifier,
    originalEdge.linkNegation
  );

  originalEdge.addDerivedEdge(derivedEdge);
  derivedEdgeArray.addEdge(derivedEdge);
  return derivedEdge;
};

/**
 * Iterate through parents and create a derived edge for each. Works with processes and subprocesses
 * @param propagate - Should be automatically propagated on diagram switch
 * @param sourceRef - Master model source mpde
 * @param targetRef - Master model target node
 * @param originalEdge - Master model original edge
 * @param sourceMain - Whether source is main diagram element (main process or its subprocess)
 * @param iterator
 */
const deriveAll = (
  propagate: boolean,
  sourceRef: MMNode,
  targetRef: MMNode,
  originalEdge: MMEdge,
  sourceMain: boolean,
  iterator: MMNode
) => {
  if (sourceNode === null || targetNode === null) return;

  if (sourceMain) {
    while (iterator !== masterModelRoot) {
      const derivedEdge = addDerivedEdge(
        iterator as MMNode,
        targetRef,
        originalEdge
      );
      if (derivedEdge) derivedEdge.propagation = propagate;
      //@ts-ignore
      iterator = iterator.parent;
    }
  } else {
    while (iterator !== masterModelRoot) {
      const derivedEdge = addDerivedEdge(
        sourceRef,
        iterator as MMNode,
        originalEdge
      );
      if (derivedEdge) derivedEdge.propagation = propagate;
      //@ts-ignore
      iterator = iterator.parent;
    }
  }
};

/**
 * Based on the current propagation, handle edge derivation.
 * Complete - all derived get propagated
 * One Level - only first derived edge gets propagated
 * None - No derived edges get propagated
 * @param sourceRef - Master model source
 * @param targetRef - Master model target
 * @param originalEdge - Master model original edge
 */
const edgeDerivation = (
  sourceRef: MMNode,
  targetRef: MMNode,
  originalEdge: MMEdge
) => {
  if (
    sourceNode === null ||
    targetNode === null ||
    currentDiagram === diagramTreeRoot
  )
    return;

  const mainNode = currentDiagram.mainNode as MMNode;

  let sourceMain, iterator;
  if (
    sourceRef === mainNode ||
    (sourceNode.isChild() && sourceNode.parent()[0].data('MMRef') === mainNode)
  ) {
    sourceMain = true;
    iterator = sourceRef.parent as MMNode;
  } else {
    sourceMain = false;
    iterator = targetRef.parent as MMNode;
  }

  if (iterator === masterModelRoot || iterator === null) return;

  if (propagation === PropagationEnum.None) {
    deriveAll(false, sourceRef, targetRef, originalEdge, sourceMain, iterator);
  } else if (propagation === PropagationEnum.Complete) {
    deriveAll(true, sourceRef, targetRef, originalEdge, sourceMain, iterator);
  } else if (propagation === PropagationEnum.OneLevel) {
    if (sourceMain) {
      const derivedEdge = addDerivedEdge(iterator, targetRef, originalEdge);
      if (derivedEdge) derivedEdge.propagation = true;
      iterator = iterator.parent as MMNode;
    } else {
      const derivedEdge = addDerivedEdge(sourceRef, iterator, originalEdge);
      if (derivedEdge) derivedEdge.propagation = true;
      iterator = iterator.parent as MMNode;
    }
    deriveAll(false, sourceRef, targetRef, originalEdge, sourceMain, iterator);
  }
};

/**
 * Additional derivation when structural parents exist or one of orignal edge endpoints is a state
 * @param originalEdge - Master model original edge
 */
const edgeDerivationStructuralAndStates = (originalEdge: MMEdge) => {
  const shouldPropagate = propagation == PropagationEnum.None ? false : true;
  let derivedEdge;
  if (originalEdge.source.isStructurePart) {
    let target;
    if (isSubElement(originalEdge.target))
      target = originalEdge.target.parent as MMNode;
    else target = originalEdge.target;

    const parent = originalEdgeArray.findStructuralParent(
      originalEdge.source
    ) as MMNode;
    const result = derivedEdgeArray.findEdgeByEndpoints(parent, target);
    if (result !== null) {
      result.originalEdges.push(originalEdge);
      derivedEdge = result;
      originalEdge.addDerivedEdge(derivedEdge);
    } else {
      let otherDerived;
      if (originalEdge.derivedEdges)
        otherDerived = originalEdge.derivedEdges[0];

      derivedEdge = addDerivedEdge(parent, target, originalEdge);
      if (derivedEdge) {
        derivedEdge.propagation = shouldPropagate;
        derivedEdge.preferedEdge = originalEdge;

        if (otherDerived !== undefined) {
          otherDerived.preferedEdge = originalEdge;
          derivedEdge.preferedEdge = otherDerived;
        }
      }
    }
  } else {
    let source;
    if (isSubElement(originalEdge.source))
      source = originalEdge.source.parent as MMNode;
    else source = originalEdge.source;
    const parent = originalEdgeArray.findStructuralParent(
      originalEdge.target
    ) as MMNode;
    const result = derivedEdgeArray.findEdgeByEndpoints(source, parent);
    if (result !== null) {
      result.originalEdges.push(originalEdge);
      derivedEdge = result;
      originalEdge.addDerivedEdge(derivedEdge);
    } else {
      let source;
      if (isSubElement(originalEdge.source))
        source = originalEdge.source.parent as MMNode;
      else source = originalEdge.source;

      let otherDerived;
      if (originalEdge.derivedEdges)
        otherDerived = originalEdge.derivedEdges[0];

      derivedEdge = addDerivedEdge(source, parent, originalEdge);
      if (derivedEdge) {
        derivedEdge.propagation = shouldPropagate;
        derivedEdge.preferedEdge = originalEdge;

        if (otherDerived !== undefined) {
          otherDerived.preferedEdge = originalEdge;
          derivedEdge.preferedEdge = otherDerived;
        }
      }
    }
  }
};

/**
 * Main edge creation function, invoked after type selection.
 * Handles model edge creation and deriving
 * @param type - Edge type
 * @param state - UI state
 */
export const edgeCreate = (
  type: EdgeType,
  state: StateInterface,
  label: string = '',
  linkQualifier: LinkQualifier = LinkQualifier.None,
  linkNegation: LinkNegation = LinkNegation.None
) => {
  if (sourceNode === null || targetNode === null) return;

  // 子图内连线不做跨图层传播/派生：仅保留当前图层连线行为。
  const isSubgraphDiagram = currentDiagram.mainNode instanceof MMNode;
  const effectivePropagation = isSubgraphDiagram
    ? PropagationEnum.None
    : state.propagation;

  const sourceRef = sourceNode.data('MMRef') as MMNode;
  const targetRef = targetNode.data('MMRef') as MMNode;

  const allowedTypes = getAllowedEdgeTypesByMMNodes(sourceRef, targetRef);
  if (!allowedTypes.includes(type)) {
    message.warning(
      getInvalidEdgeMessageByMMNodes(sourceRef, targetRef) ||
        '当前节点组合不支持所选连线类型'
    );
    targetNode.removeClass('eh-hover');
    sourceNode = null;
    targetNode = null;
    return;
  }

  const counter = eleCounter.value;
  // 结构关系不允许填写 label（保持与旧版编辑器一致）
  const canEditLabel = !hierarchicalStructuralEdges.includes(type);
  const sanitizedLabel = canEditLabel ? label : '';
  const existingVisibleEdge = findExistingVisibleEdge(
    sourceRef,
    targetRef,
    type,
    sanitizedLabel,
    linkQualifier,
    linkNegation
  );
  if (existingVisibleEdge) {
    message.info('当前连线已存在，无需重复创建');
    targetNode.removeClass('eh-hover');
    sourceNode = null;
    targetNode = null;
    return;
  }
  const data = {
    id: counter,
    label: sanitizedLabel,
    type: type,
    MMRef: null,
    source: sourceRef,
    target: targetRef,
    linkQualifier,
    linkNegation,
    markerColor: '#586D8C',
  };

  const shouldPropagate =
    effectivePropagation == PropagationEnum.None ? false : true;

  // 结构关系：同向同端点只保留一条，新增前先清理旧的
  if (hierarchicalStructuralEdges.includes(type)) {
    removeExistingStructuralEdge(sourceRef, targetRef);
  }

  const addedEdge = addEdge(cy, data, shouldPropagate);
  if (addedEdge === null) return;
  const addedEdgeRef = addedEdge.data('MMRef');

  if (!isSubgraphDiagram) {
    if (hierarchicalStructuralEdges.includes(type)) {
      targetRef.isStructurePart = true;
    } else if (
      sourceRef.isStructurePart ||
      sourceRef.isSubelementOfMain ||
      targetRef.isStructurePart ||
      targetRef.isSubelementOfMain
    ) {
      if (!areRelated(sourceRef, targetRef)) {
        edgeDerivation(sourceRef, targetRef, addedEdgeRef);
      }
      if (
        sourceRef.isStructurePart === true ||
        targetRef.isStructurePart === true
      ) {
        edgeDerivationStructuralAndStates(addedEdgeRef);
      }
    }
  }

  targetNode.removeClass('eh-hover');
  sourceNode = null;
  targetNode = null;
};

/**
 * Main reconnection function. Handles deletion of old edges and creation of new ones
 * as well as deriving. Called from the cytoscape-edgehandles extension
 * @param sourceID - Source node id
 * @param targetID - Target node id
 * @param data - cytoscape edge data
 */
export const edgeReconnect = (
  sourceID: string,
  targetID: string,
  data: any
) => {
  if (sourceID === targetID) return;

  const edgeId = data['id'];
  const MMRef = data['MMRef'] as MMEdge;
  const edge = cy.getElementById(edgeId);

  sourceNode = cy.getElementById(sourceID);
  targetNode = cy.getElementById(targetID);
  const sourceRef = sourceNode?.data('MMRef') as MMNode;
  const targetRef = targetNode?.data('MMRef') as MMNode;

  const isSubgraphDiagram = currentDiagram.mainNode instanceof MMNode;
  if (isSubgraphDiagram) {
    const edgeIdValue = String(edgeId);
    const isSharedWithOtherDiagram = (() => {
      let shared = false;
      const walk = (diagram: any) => {
        if (!diagram || shared) return;
        if (diagram !== currentDiagram) {
          const edges = diagram.diagramJson?.elements?.edges || [];
          const hit = edges.some((item: any) => {
            const edgeData = item?.data || {};
            const id = edgeData.id || edgeData.MMRef?.id;
            return String(id) === edgeIdValue;
          });
          if (hit) {
            shared = true;
            return;
          }
        }
        (diagram.children || []).forEach((child: any) => walk(child));
      };
      walk(diagramTreeRoot);
      return shared;
    })();

    // 兼容历史数据：若子图边仍与其他图层共享同一 MMEdge，重连时先转成本图层本地边。
    if (isSharedWithOtherDiagram) {
      const baseLabel = MMRef.originalEdges.length
        ? MMRef.originalEdges[0].label || ''
        : MMRef.label || '';
      edge.remove();
      addEdge(
        cy,
        {
          id: eleCounter.value,
          label: baseLabel,
          type: MMRef.type,
          MMRef: null,
          source: sourceRef,
          target: targetRef,
          linkQualifier: MMRef.linkQualifier,
          linkNegation: MMRef.linkNegation,
          markerColor: '#586D8C',
        },
        false
      );
      sourceNode = null;
      targetNode = null;
      return;
    }
  }

  MMRef.source = sourceRef;
  MMRef.target = targetRef;

  if (MMRef.originalEdges.length) {
    originalEdgeArray.removeOriginalEdges(MMRef.originalEdges);
    for (const edge of MMRef.originalEdges) {
      edge.removeAllDerived();
    }
    MMRef.deleted = false;
    MMRef.originalEdges = [];
    originalEdgeArray.addEdge(MMRef);
  } else {
    MMRef.removeAllDerived();
  }

  const shouldPropagate =
    !isSubgraphDiagram && propagation !== PropagationEnum.None;
  MMRef.propagation = shouldPropagate;

  if (!isSubgraphDiagram) {
    if (hierarchicalStructuralEdges.includes(MMRef.type)) {
      targetRef.isStructurePart = true;
    } else if (
      sourceRef.isStructurePart ||
      sourceRef.isSubelementOfMain ||
      targetRef.isStructurePart ||
      targetRef.isSubelementOfMain
    ) {
      if (!areRelated(sourceRef, targetRef)) {
        edgeDerivation(sourceRef, targetRef, MMRef);
      }
      if (
        sourceRef.isStructurePart === true ||
        targetRef.isStructurePart === true
      ) {
        edgeDerivationStructuralAndStates(MMRef);
      }
    }
  }

  edge.move({
    source: sourceID,
    target: targetID,
  });
  refreshStructuralEdgeStyles(cy, edge);
};
